import { useMemo } from 'react';
import { useQuery } from 'react-query';
import PESService from 'App/services/PESService';
import { formatActivities, formatReferrals } from 'App/components/PatientProfile/PatientProfile.utils';
import useFetchPatientReferralsQuery from 'App/hooks/useFetchPatientReferralsQuery';

const ACTIVITY_STATUS_LOOKUP_IDS_BY_STATUS = {
    pending: ['0', '3'],
    completed: ['1'],
    deleted: ['2'],
};

/**
 * Custom hook which fetches a single status segment (pending/completed/deleted) of a patient's
 * activities, merged with any referral-derived activities that fall into that same segment.
 * @param {string} patientId
 * @param {string} status - 'pending' | 'completed' | 'deleted'
 * @returns {Object}
 */
function useGetPatientActivities(patientId, status) {
    const allowedStatusLookupIds = useMemo(() => ACTIVITY_STATUS_LOOKUP_IDS_BY_STATUS[status] || [], [status]);
    const isEnabled = Boolean(patientId && allowedStatusLookupIds.length);

    const { data: activityResult, isLoading: isActivityQueryLoading } = useQuery(
        ['patient', patientId, 'activities', status],
        () => PESService.getActivities(patientId, status),
        {
            enabled: isEnabled,
        }
    );

    const { data: referralResult = [], isLoading: isReferralQueryLoading } = useFetchPatientReferralsQuery(patientId, {
        enabled: isEnabled,
    });

    const activities = useMemo(() => {
        if (!isEnabled) {
            return [];
        }

        const referralChain = formatReferrals(referralResult || []);
        const activityList = formatActivities(activityResult || []);

        for (const referral of referralChain) {
            const referralActivities = Array.isArray(referral.activities) ? referral.activities : [];
            activityList.push(
                ...referralActivities.filter((activity) =>
                    allowedStatusLookupIds.includes(activity.activityStatusLookupId)
                )
            );
        }

        return activityList;
    }, [activityResult, referralResult, allowedStatusLookupIds, isEnabled]);

    const isActivityLoading = isActivityQueryLoading || isReferralQueryLoading;

    return useMemo(
        () => ({
            activities,
            isActivityLoading,
        }),
        [activities, isActivityLoading]
    );
}

export default useGetPatientActivities;
